import type { FC } from 'react'
import React, { useEffect, useMemo, useRef } from 'react'

import type {
  SelectionChangedEvent,
  TabsContext,
  TabsContextProviderProps
} from './types'
import useControllableState from '../../hooks/use-controllable'
import { __DEV__ } from '../../utils/assertion'
import { createContext } from '../../utils/react-helpers'
import { EventObserver } from '../_utils'

const [TabsProvider, useTabsContext] = createContext<TabsContext>({
  name: 'TabsContext'
})

const defaultScrollIntoViewOptions: ScrollIntoViewOptions = {
  behavior: 'smooth',
  block: 'start'
}

export const TabsContextProvider: FC<TabsContextProviderProps> = props => {
  const {
    initialSelection = 0,
    selection: selectionProp,
    onChange,
    variant,
    children,
    mode = 'switch',
    scrollIntoViewOptions = defaultScrollIntoViewOptions
  } = props

  const [selection, setSelection] = useControllableState({
    value: selectionProp,
    defaultValue: initialSelection,
    onChange: onChange
  }).state

  const selectionChangedObserver = useMemo(() => {
    return new EventObserver<
      (event: SelectionChangedEvent) => void,
      SelectionChangedEvent
    >('tabs-selection-changed')
  }, [])
  const isScrolling = useRef(false)
  const isSettingTab = useRef(false)
  const scrollTimer = useRef<NodeJS.Timeout>()
  const settingTabTimer = useRef<NodeJS.Timeout>()
  useEffect(() => {
    const handleSelectionChange = (event: SelectionChangedEvent) => {
      if (mode === 'stack' && event.source === 'tabs') {
        setSelection(event.value)
        document
          .getElementById(`nav-scrollto-${event.value}`)
          ?.scrollIntoView(
            scrollIntoViewOptions === undefined ||
              scrollIntoViewOptions === true
              ? defaultScrollIntoViewOptions
              : scrollIntoViewOptions
          )
        scrollTimer.current && clearTimeout(scrollTimer.current)
        isScrolling.current = true
        scrollTimer.current = setTimeout(() => {
          isScrolling.current = false
        }, 1000)
      }
      if (
        mode === 'switch' ||
        (mode === 'stack' && event.source === 'panels' && !isScrolling.current)
      ) {
        // if we receive multiple events within a 50ms period, only handle the first one
        if (isSettingTab.current) return
        isSettingTab.current = true
        settingTabTimer.current = setTimeout(() => {
          isSettingTab.current = false
        }, 50)
        setSelection(event.value)
      }
    }
    selectionChangedObserver.attach(handleSelectionChange)
    return () => selectionChangedObserver.detach(handleSelectionChange)
  }, [selectionChangedObserver, setSelection, mode, scrollIntoViewOptions])
  const context = useMemo(
    () => ({
      variant,
      selectionChangedObserver,
      selection: selection,
      controlledSelection: selection,
      mode
    }),
    [variant, selectionChangedObserver, selection, mode]
  )
  useEffect(() => {
    return () => {
      scrollTimer.current && clearTimeout(scrollTimer.current)
      settingTabTimer.current && clearTimeout(settingTabTimer.current)
    }
  }, [])

  return <TabsProvider value={context}>{children}</TabsProvider>
}

if (__DEV__) {
  TabsContextProvider.displayName = 'TabsContextProvider'
}

export { useTabsContext }
